import { VISIBLE_MASTER_DEFINITIONS } from '@/features/masters';
import type { MatrixGroup, MatrixItem, PermissionMatrix } from './rbac.types';

/**
 * Makes the Masters section of the permission matrix the Masters menu: the same entries, in the same order.
 *
 * <h2>Why the platform's order is not used</h2>
 *
 * <p>The matrix arrives in whatever order `os_feature` yields, which is the order the rows were seeded and
 * then extended -- Organization, Entity, ..., User Management, then Designation, which joined later. The
 * Masters menu, the masters landing page and every breadcrumb use a deliberate order: Organization first,
 * then the hierarchy, then the commercial masters, ending at User Management. An administrator ticking
 * permissions is reading the same list of names they navigate by all day, and two different orders for one
 * list is a reason to lose your place in it. `MASTER_DEFINITIONS` is kept in the menu's order, so its
 * index is the position.
 *
 * <h2>Only the Masters group is touched</h2>
 *
 * <p>Functions and Templates have no menu to agree with, so their order is the platform's and is left
 * exactly as it arrives. Reordering them would be a change with nothing behind it.
 *
 * <h2>A MASTER row with no screen is set aside, not dropped</h2>
 *
 * <p>Asked for: the Masters group lists exactly what the Masters menu lists. So a MASTER feature no menu
 * entry names -- one the platform added before this client learned of it -- is not drawn. It is not
 * discarded either: the save replaces a role's whole matrix, and an item missing from the request would read
 * as "revoke everything on it". Such rows travel in `retained` and are sent back exactly as they were read,
 * so hiding one never changes what any role holds.
 *
 * <h2>Country, State and City</h2>
 *
 * <p>That same rule is what takes the three geography masters off this screen. Review sheet #12 made them
 * seed data and took them out of the Masters menu, so by "exactly what the menu lists" their rows are not
 * drawn -- and because an undrawn row is retained rather than dropped, every role keeps the Country, State
 * and City grants it already holds, and a save from this screen sends them back untouched. The position map
 * is therefore built from `VISIBLE_MASTER_DEFINITIONS`, not from the full registry: the registry still holds
 * all nineteen, because the routes and screens behind them are unchanged.
 */

/** Feature name -> its position in the Masters menu. */
const MENU_POSITION = new Map<string, number>(
  VISIBLE_MASTER_DEFINITIONS.map((definition, index) => [definition.feature.toLowerCase(), index]),
);

function positionOf(name: string): number | undefined {
  return MENU_POSITION.get(name.trim().toLowerCase());
}

/** Whether a MASTER feature has a Masters menu entry -- and so a row in the matrix. */
export function isMenuMaster(name: string): boolean {
  return positionOf(name) !== undefined;
}

function ordered(group: MatrixGroup): { group: MatrixGroup; retained: MatrixItem[] } {
  const shown = group.items.filter((item) => isMenuMaster(item.name));
  const retained = group.items.filter((item) => !isMenuMaster(item.name));

  const items = [...shown].sort(
    (left, right) => (positionOf(left.name) ?? 0) - (positionOf(right.name) ?? 0),
  );
  return { group: { ...group, items }, retained };
}

export function inMastersMenuOrder(matrix: PermissionMatrix): PermissionMatrix {
  const retained: MatrixItem[] = [...(matrix.retained ?? [])];

  const groups = matrix.groups.map((group) => {
    if (group.key !== 'MASTER') return group;
    const result = ordered(group);
    retained.push(...result.retained);
    return result.group;
  });

  return retained.length === 0 ? { ...matrix, groups } : { ...matrix, groups, retained };
}
